import { UISref, useRouter } from "@uirouter/react";
import styled from "styled-components";
import moment from "moment";
import { FunctionComponent, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { PrimaryButton } from "../../../../components/button";
import { Icon } from "../../../../components/icons";
import { Loader } from "../../../../components/loader";
import { Table } from "../../../../components/table";
import { TableHeaders } from "../../../../components/table/Head";
import { Widget } from "../../../../components/widget";
import { Pageable } from "../../../../services/pageable";
import { AppStore } from "../../../../store";
import { useFetchable } from "../../../../store/common/fetchable";
import { CryptoActions } from "../../../../store/crypto/actions";
import { CryptoDTO } from "../../../../store/crypto/models/dto";
import { Crypto } from "../../../../store/crypto/reducer";
import { colours } from "../../../../styles/colours";
import { BlockchainToolsRoutes } from "../../routes";
import { NoFraudReports } from "./empty";
import { device } from "../../../../styles/media";
import { useTranslation } from "react-i18next";

type SingleFraudReport = CryptoDTO.Responses.GetSingleFraudReport.T;
type fieldNames = keyof SingleFraudReport;

const AddressWrapper = styled.div`
  overflow-wrap: break-word;
  width: 9em;

  @media ${device.laptop} {
    width: auto;
  }
`;

const AmountWrapper = styled.div`
  overflow-wrap: break-word;
  width: 4em;

  @media ${device.laptop} {
    width: auto;
  }
`;

export const AllFraudReport = () => {
  const data = useFetchable(
    Crypto.allFraudReports,
    useMemo<Pageable.T<keyof CryptoDTO.Responses.GetSingleFraudReport.T>>(
      () => ({
        pagination: {
          size: 20,
          page: 0,
        },
        sorting: {
          direction: "desc",
          field: "createdAt",
        },
      }),
      []
    )
  );

  return (
    <Loader data={[data]}>
      {([fulfilled]) => (
        <PresentationalAllFraudReport fraudReportList={fulfilled} />
      )}
    </Loader>
  );
};

const PresentationalAllFraudReport: FunctionComponent<{
  fraudReportList: CryptoDTO.Responses.GetAllFraudReport.T;
}> = ({ fraudReportList }) => {
  const dispatch = useDispatch();
  const router = useRouter();

  const { labels } = CryptoDTO.Responses.GetSingleFraudReport.useFields();

  const headers: TableHeaders<fieldNames> = useMemo(
    () => [
      {
        name: "address",
        title: labels.address,
      },
      {
        name: "fraudCurrency",
        title: labels.fraudCurrency,
      },
      {
        name: "fraudAmount",
        title: labels.fraudAmount,
      },
      {
        name: "createdAt",
        title: labels.createdAt,
      },
      {
        name: "id",
        title: "",
        centered: true,
        hiddenMobile: true,
      },
    ],
    [labels]
  );

  const pageableState = useSelector<
    AppStore,
    AppStore["crypto"]["allFraudReport"]["pagination"]
  >((store) => store.crypto.allFraudReport.pagination);

  const onPaginationChange = (x: Pageable.T<fieldNames>) =>
    dispatch(CryptoActions.setAllFraudReportPagination(x));

  const { t } = useTranslation("blockhain-tools");

  return (
    <Widget title={t("all-fraud-reports")}>
      {fraudReportList.pageable.totalElements < 1 ? (
        <NoFraudReports />
      ) : (
        <Table
          data={fraudReportList.content}
          head={headers}
          renderFields={{
            createdAt: ({ createdAt }) => (
              <>{moment.utc(createdAt).local().format("yyy-MM-DD HH:mm")}</>
            ),
            fraudAmount: ({ fraudAmount, fraudCurrency }) => (
              <AmountWrapper>
                {fraudCurrency} {fraudAmount}
              </AmountWrapper>
            ),
            id: ({ id }) => (
              <UISref
                to={BlockchainToolsRoutes.names.reportSingle}
                params={{ id }}
              >
                <a>
                  <PrimaryButton variant="contained">
                    <Icon
                      type="export"
                      rotateAmount={270}
                      colour1={colours.white}
                    />
                  </PrimaryButton>
                </a>
              </UISref>
            ),
            address: ({ address }) => (
              <AddressWrapper>{address}</AddressWrapper>
            ),
          }}
          rowOnClick={({ id }) => {
            router.stateService.go(BlockchainToolsRoutes.names.reportSingle, {
              id,
            });
          }}
          hide={["fraudCurrency"]}
          pagination={fraudReportList.pageable}
          onPaginationChange={onPaginationChange}
          pageableState={pageableState}
        />
      )}
    </Widget>
  );
};
